<template>
  <VRow no-gutters class="profile-row">
    <VCol v-if="label && !filled" cols="12" :class="['profile-label', isFocused && 'focused', prependIcon && 'pl-8']">
      <span v-if="!$scopedSlots['infotext']" v-text="label" />
      <InfoBubble v-else :tooltipPosition="tooltipPosition">
        <span v-text="label" />
        <template #infotext>
          <slot name="infotext" />
        </template>
      </InfoBubble>
    </VCol>
    <VCol cols="12">
      <VTextField
        v-bind="$attrs"
        ref="inner"
        :class="['m-text-field', 'm-common', clickable && 'clickable']"
        hide-details="auto"
        autocomplete="new-password"
        flat
        :type="proxyType"
        :loader-height="1"
        :prepend-icon="prependIcon"
        :solo="!filled"
        :filled="filled"
        :aria-label="$attrs['aria-label'] || label"
        :label="filled ? label : ''"
        v-on="$listeners"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
        <template v-if="isPassword && showReveal" #append>
          <VIcon v-tooltip="showPasswordTooltip" class="mr-1" style="cursor:pointer" @click="showingSecret = !showingSecret">{{ showingSecret ? 'visibility_off' : 'visibility' }}</VIcon>
          <VIcon v-if="validated" v-tooltip="{ disabled: !validatedTooltip, text: validatedTooltip }" color="success" class="ml-1">check</VIcon>
        </template>
        <template v-for="(_, scopedSlotName) in $scopedSlots" #[scopedSlotName]="slotData">
          <slot :name="scopedSlotName" v-bind="slotData" />
        </template>
        <template v-for="(_, slotName) in $slots" #[slotName]>
          <slot :name="slotName" />
        </template>
      </VTextField>
    </VCol>
  </VRow>
</template>

<style scoped lang="stylus">
:deep(.v-input--is-disabled) .v-input__slot,
:deep(.v-input--is-readonly):not(.clickable) .v-input__slot
  opacity: .5
  input
    color: var(--v-default-base)
  .profile-row
    font-size: 14px
    white-space: pre-wrap
    overflow-wrap: break-word
    .profile-label
      font-size: 12px
      color: var(--v-primary-base)
      opacity: 0.8
      &.focused,
      .high-contrast
        color: var(--v-strong-base)
        opacity: 1
.clickable
  :deep(input)
    cursor: pointer
:deep(.v-text-field--filled)
  .v-input__append-inner
    margin-top: 7px
  .v-input__prepend-outer
    margin-top: 15px
  .v-input__slot
    padding: 0px 4px !important
    min-height: 36px !important
    border-radius: 2px !important
    background: var(--v-ternaryBackground-base) !important
    input
      color: var(--v-default-base)
  .v-label
    color: var(--v-default-base)
    top: 11px
    font-size: 13px !important
    line-height: 16px
  .v-label--active
    color: var(--v-default-base)
    top: 9px
    font-size: 11px !important
  input
    padding: 0 !important
    margin-top: 10px !important
    max-height: 36px !important
  .v-input__slot:before, .v-input__slot:after
    display: none
</style>

<script lang="ts">
import type { PropType } from '@mimir/web/compat/vueCompat';
import { defineComponent } from '@mimir/web/compat/vueCompat';
import { VCol, VIcon, VRow, VTextField } from 'vuetify/lib';

export default defineComponent({
  name: 'MTextField',
  components: {
    VCol,
    VIcon,
    VRow,
    VTextField,
  },
  inheritAttrs: false,
  props: {
    label: {
      type: String as PropType<string>
    },
    validatedTooltip: {
      type: String as PropType<string>
    },
    prependIcon: {
      type: String as PropType<string>
    },
    tooltipPosition: {
      type: String as PropType<string>
    },
    validated: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    isPassword: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    showReveal: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    filled: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    clickable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    type: {
      type: String as PropType<string>,
      default: 'text'
    }
  },
  data() {
    return {
      isFocused: false,
      showingSecret: false
    };
  },
  computed: {
    proxyType(): string {
      if (!this.isPassword) {
        return this.type;
      }
      return this.showingSecret ? this.type : 'password';
    },
    showPasswordTooltip(): string {
      return this.showingSecret ? 'Hide' : 'Reveal';
    }
  },
  methods: {
    focus() {
      (this.$refs.inner as any).focus();
    }
  },
});

</script>
